import { db } from '@backend/db/db'
import { useDocument } from '@backend/db/hooks/useDocument'
import { getPracticeEncountersQuery } from '@backend/db/queries/practice'
import { updateEncounterBillingFields } from '@backend/functions/gyn'
import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  HStack,
  Input,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { useApp, useAuth, usePopUpMessage, usePracticeAccess } from '@hb/react'
import {
  CLAIMS,
  computeBatchRecommendations,
  dosColorScheme,
  Encounter,
  EncounterStatus,
  EncounterType,
  encounterTypeName,
  FacilityClaim,
  getDateString,
  getDaysSinceDOS,
  getNotificationLevel,
  INSURANCE_PROVIDERS,
  InsuranceProvider,
  MIDWIVES,
  NotificationLevel,
  Practice,
  PracticeVisit,
  TelephoneContact,
  VISITS,
  WithId,
} from '@hb/shared'
import { useQuery } from '@hooks/backend/useQuery'
import { usePracticeContactsForReview } from '@hooks/backend/user/usePracticeContactsForReview'
import { useUserItem } from '@hooks/backend/user/useUserFromId'
import { useNavigate } from '@hooks/navigation'
import {
  collection,
  CollectionReference,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useEncounterStatusLabel } from '../../charting/Encounters/useEncounterStatusLabel'

// ─── Helpers (getDaysSinceDOS, dosColorScheme imported from @hb/shared) ────────

const authColorScheme: Record<EncounterStatus, string> = {
  [EncounterStatus.DRAFT]: 'orange',
  [EncounterStatus.COMPLETED]: 'green',
  [EncounterStatus.AUTHENTICATED]: 'blue',
}

// ─── Patient name cell ────────────────────────────────────────────────────────

const PatientNameCell = ({ patientId }: { patientId: string }) => {
  const user = useUserItem(patientId)
  const name = user ? `${user.fname ?? ''} ${user.lname ?? ''}`.trim() : null
  return (
    <Text fontSize="sm" fontWeight={500} color={name ? 'gray.800' : 'gray.400'}>
      {name ?? patientId.slice(0, 8) + '…'}
    </Text>
  )
}

// ─── Single encounter row ─────────────────────────────────────────────────────

const BillingEncounterRow = ({
  encounter,
  timelyFilingDaysMap,
}: {
  encounter: WithId<Encounter>
  timelyFilingDaysMap: Map<string, number>
}) => {
  const navigate = useNavigate()
  const statusLabel = useEncounterStatusLabel(encounter.status)
  const daysSinceDOS = getDaysSinceDOS(encounter.date)
  const timelyFilingDays = timelyFilingDaysMap.get(encounter.id) ?? 90
  const daysRemaining = daysSinceDOS !== null ? timelyFilingDays - daysSinceDOS : null

  const hasAmendmentAlert =
    (encounter.amendments?.length ?? 0) > 0 &&
    encounter.billingReviewedOn !== undefined &&
    (encounter.amendments?.some(a => a.on > (encounter.billingReviewedOn ?? 0)) ?? false)

  return (
    <Tr
      _hover={{ bg: 'blue.50', cursor: 'pointer' }}
      onClick={() => navigate(`/billing/${encounter.id}`)}>
      <Td py={2}>
        <PatientNameCell patientId={encounter.patientId} />
      </Td>
      <Td py={2} fontSize="sm" color="gray.700">
        {encounter.date ?? '—'}
      </Td>
      <Td py={2}>
        {daysSinceDOS !== null ? (
          <Badge colorScheme={dosColorScheme(daysSinceDOS)} fontSize="xs">
            {daysSinceDOS}d since DOS
          </Badge>
        ) : (
          <Text fontSize="xs" color="gray.400">
            —
          </Text>
        )}
      </Td>
      <Td py={2}>
        <HStack spacing={1}>
          <Badge colorScheme={authColorScheme[encounter.status]} fontSize="xs">
            {statusLabel}
          </Badge>
          {hasAmendmentAlert ? (
            <Badge colorScheme="red" fontSize="xs">
              AMENDED
            </Badge>
          ) : null}
          {(encounter.data as any)?.quickNoteOrigin ? (
            <Badge colorScheme="orange" fontSize="xs">
              Quick Note — incomplete
            </Badge>
          ) : null}
          {(encounter.data as any)?.doNotBill ? (
            <Badge colorScheme="gray" fontSize="xs">
              Provider Hold — Do Not Bill
            </Badge>
          ) : null}
        </HStack>
      </Td>
      <Td py={2} fontSize="sm" color="gray.600">
        {encounterTypeName[encounter.type] ?? encounter.type}
      </Td>
      <Td py={2} fontSize="sm" color="gray.600">
        {encounter.renderingProviderInitials ?? encounter.renderingProvider ?? '—'}
      </Td>
      <Td py={2}>
        {daysRemaining !== null ? (
          <Text
            fontSize="xs"
            fontWeight={500}
            color={
              daysRemaining <= 0 ? 'red.700' : daysRemaining <= 25 ? 'orange.600' : 'gray.500'
            }>
            {daysRemaining <= 0
              ? `${Math.abs(daysRemaining)}d overdue`
              : `${daysRemaining}d remaining`}
          </Text>
        ) : (
          <Text fontSize="xs" color="gray.400">
            —
          </Text>
        )}
      </Td>
    </Tr>
  )
}

// ─── Encounter table ──────────────────────────────────────────────────────────

const BillingEncounterTable = ({
  encounters,
  timelyFilingDaysMap,
  emptyMessage,
}: {
  encounters: WithId<Encounter>[]
  timelyFilingDaysMap: Map<string, number>
  emptyMessage: string
}) => {
  if (encounters.length === 0) {
    return (
      <Center py={10}>
        <Text color="gray.500">{emptyMessage}</Text>
      </Center>
    )
  }
  return (
    <Box overflowX="auto" w="100%">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th py={2}>Patient</Th>
            <Th py={2}>DOS</Th>
            <Th py={2}>Days Since DOS</Th>
            <Th py={2}>Auth Status</Th>
            <Th py={2}>Visit Type</Th>
            <Th py={2}>Provider</Th>
            <Th py={2}>Timely Filing</Th>
          </Tr>
        </Thead>
        <Tbody>
          {encounters.map(enc => (
            <BillingEncounterRow
              key={enc.id}
              encounter={enc}
              timelyFilingDaysMap={timelyFilingDaysMap}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

// ─── §6.4.2: Notification log + outreach logging ──────────────────────────────

const OutreachLogForm = ({ encounterId }: { encounterId: string }) => {
  const { authUser } = useAuth()
  const { appName } = useApp()
  const { showError } = usePopUpMessage()
  const [outcome, setOutcome] = useState('')
  const [saving, setSaving] = useState(false)
  const [open, setOpen] = useState(false)

  const save = useCallback(async () => {
    if (!outcome.trim() || !authUser?.uid) return
    setSaving(true)
    const entry = { on: Date.now(), by: authUser.uid, outcome: outcome.trim() }
    // §6.4.2: APPEND one entry — the callable uses arrayUnion server-side so existing outreach
    // history is preserved (the old direct write overwrote the whole array). Firestore rules
    // allow no client writes to ENCOUNTERS. Surface errors instead of closing as if it saved.
    try {
      await updateEncounterBillingFields({
        encounterId,
        appendOutreachLogEntry: entry,
        appName,
      })
      setOutcome('')
      setOpen(false)
    } catch (err) {
      showError((err as Error)?.message ?? 'Could not log the outreach entry.')
    } finally {
      setSaving(false)
    }
  }, [encounterId, authUser, outcome, appName, showError])

  if (!open) {
    return (
      <Button size="xs" variant="outline" colorScheme="purple" onClick={() => setOpen(true)}>
        + Log Outreach
      </Button>
    )
  }

  return (
    <HStack spacing={2}>
      <Input
        size="xs"
        placeholder="Outreach outcome..."
        value={outcome}
        onChange={e => setOutcome(e.target.value)}
        bg="white"
        w="220px"
      />
      <Button
        size="xs"
        colorScheme="purple"
        isLoading={saving}
        isDisabled={!outcome.trim()}
        onClick={save}>
        Save
      </Button>
      <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </HStack>
  )
}

const notificationLevelLabel: Record<NotificationLevel, string> = {
  'billing-alert': 'Billing Alert (Day 59+)',
  'critical-notice': 'Critical Notice (Day 52+)',
  'first-notice': 'First Notice (Day 45+)',
}

const notificationColorScheme: Record<NotificationLevel, string> = {
  'billing-alert': 'red',
  'critical-notice': 'orange',
  'first-notice': 'yellow',
}

const NotificationRow = ({
  encounter,
  level,
  daysSinceDOS,
}: {
  encounter: WithId<Encounter>
  level: NotificationLevel
  daysSinceDOS: number
}) => {
  const outreachCount = encounter.outreachLog?.length ?? 0
  return (
    <Tr>
      <Td py={2}>
        <PatientNameCell patientId={encounter.patientId} />
      </Td>
      <Td py={2} fontSize="sm" color="gray.700">
        {encounter.date ?? '—'}
      </Td>
      <Td py={2} fontSize="sm" color="gray.700">
        {encounter.renderingProviderInitials ?? encounter.renderingProvider ?? '—'}
      </Td>
      <Td py={2}>
        <Badge colorScheme={notificationColorScheme[level]} fontSize="xs">
          {notificationLevelLabel[level]}
        </Badge>
      </Td>
      <Td py={2}>
        <Text fontSize="xs" color="gray.600">
          {daysSinceDOS}d since DOS
        </Text>
      </Td>
      <Td py={2}>
        <HStack spacing={2}>
          {outreachCount > 0 && (
            <Badge colorScheme="purple" fontSize="xs">
              {outreachCount} outreach logged
            </Badge>
          )}
          {level === 'billing-alert' && <OutreachLogForm encounterId={encounter.id} />}
        </HStack>
      </Td>
    </Tr>
  )
}

const NotificationLogPanel = ({
  encounters,
  thresholds,
}: {
  encounters: WithId<Encounter>[]
  thresholds: { firstNotice: number; criticalNotice: number; billingAlert: number }
}) => {
  const notificationEncounters = useMemo(
    () =>
      encounters
        .filter(enc => enc.status !== EncounterStatus.AUTHENTICATED)
        .flatMap(enc => {
          const days = getDaysSinceDOS(enc.date)
          if (days === null) return []
          const level = getNotificationLevel(days, thresholds)
          if (!level) return []
          return [{ encounter: enc, level, daysSinceDOS: days }]
        })
        .sort((a, b) => b.daysSinceDOS - a.daysSinceDOS),
    [encounters, thresholds],
  )

  if (notificationEncounters.length === 0) {
    return (
      <Center py={10}>
        <Text color="gray.500">No encounters have crossed notification thresholds.</Text>
      </Center>
    )
  }

  return (
    <Box overflowX="auto" w="100%">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th py={2}>Patient</Th>
            <Th py={2}>DOS</Th>
            <Th py={2}>Provider</Th>
            <Th py={2}>Alert Level</Th>
            <Th py={2}>Days Since DOS</Th>
            <Th py={2}>Outreach</Th>
          </Tr>
        </Thead>
        <Tbody>
          {notificationEncounters.map(({ encounter, level, daysSinceDOS }) => (
            <NotificationRow
              key={encounter.id}
              encounter={encounter}
              level={level}
              daysSinceDOS={daysSinceDOS}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

// ─── §6.4.3: Batch Recommendations (logic in @hb/shared computeBatchRecommendations) ──

// BatchGroup extends BatchRecommendation to carry full encounter objects for navigation.
interface BatchGroup {
  patientId: string
  encounterType: EncounterType
  encounters: WithId<Encounter>[]
  oldestDays: number
  reason: string
}

const buildBatchGroups = (encounters: WithId<Encounter>[]): BatchGroup[] => {
  const recs = computeBatchRecommendations(encounters)
  // Re-attach full encounter objects for the navigate-to-first-encounter action.
  const byId = new Map(encounters.map(e => [e.id, e]))
  return recs.map(r => ({
    patientId: r.patientId,
    encounterType: r.encounterType,
    encounters: r.encounterIds.map(id => byId.get(id)).filter(Boolean) as WithId<Encounter>[],
    oldestDays: r.oldestDays,
    reason: r.reason,
  }))
}

const BatchRow = ({ group, navigate }: { group: BatchGroup; navigate: (path: string) => void }) => (
  <Tr>
    <Td py={2}>
      <PatientNameCell patientId={group.patientId} />
    </Td>
    <Td py={2} fontSize="sm" color="gray.600">
      {encounterTypeName[group.encounterType] ?? group.encounterType}
    </Td>
    <Td py={2}>
      <Badge colorScheme="blue" fontSize="xs">
        {group.encounters.length} visit{group.encounters.length !== 1 ? 's' : ''}
      </Badge>
    </Td>
    <Td py={2}>
      <Badge colorScheme={group.oldestDays >= 60 ? 'red' : 'orange'} fontSize="xs">
        {group.oldestDays}d oldest
      </Badge>
    </Td>
    <Td py={2} fontSize="xs" color="gray.700">
      {group.reason}
    </Td>
    <Td py={2}>
      <Button
        size="xs"
        variant="outline"
        colorScheme="green"
        onClick={() => navigate(`/billing/${group.encounters[0].id}`)}>
        Review
      </Button>
    </Td>
  </Tr>
)

const BatchRecommendationsPanel = ({ encounters }: { encounters: WithId<Encounter>[] }) => {
  const navigate = useNavigate()
  const groups = useMemo(() => buildBatchGroups(encounters), [encounters])

  if (groups.length === 0) {
    return (
      <Center py={10}>
        <Text color="gray.500">No batch recommendations at this time.</Text>
      </Center>
    )
  }

  return (
    <Box overflowX="auto" w="100%">
      <Box px={4} py={2} bg="blue.50" borderBottom="1px solid" borderColor="blue.100">
        <Text fontSize="xs" color="blue.700">
          System recommends — billing team approves. System never auto-submits.
        </Text>
      </Box>
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th py={2}>Patient</Th>
            <Th py={2}>Visit Type</Th>
            <Th py={2}>Count</Th>
            <Th py={2}>Oldest Visit</Th>
            <Th py={2}>Recommendation</Th>
            <Th py={2}>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {groups.map((g, i) => (
            <BatchRow
              key={`${g.patientId}_${g.encounterType}_${i}`}
              group={g}
              navigate={navigate}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

// ─── §2.17: Contacts for review ──────────────────────────────────────────────

const contactTypeLabels: Record<string, string> = {
  'phone-call': 'Phone',
  'video-call': 'Video',
  'in-person-without-visit': 'In-Person',
  'after-hours-contact': 'After Hours',
  'text-message': 'Text',
  'patient-portal-message': 'Portal Message',
}

const followUpLabels: Record<string, string> = {
  none: 'None',
  'visit-scheduled': 'Visit Scheduled',
  prescription: 'Prescription',
  referral: 'Referral',
  'er-advised': 'ER Advised',
  other: 'Other',
}

const ContactRow = ({
  contact,
  navigate,
}: {
  contact: WithId<TelephoneContact>
  navigate: (path: string) => void
}) => (
  <Tr
    _hover={{ bg: 'blue.50', cursor: 'pointer' }}
    onClick={() => navigate(`/patients/${contact.patientId}`)}>
    <Td py={2} fontSize="sm" color="gray.700">
      {getDateString(contact.date, 'short', true)}
    </Td>
    <Td py={2}>
      <PatientNameCell patientId={contact.patientId} />
    </Td>
    <Td py={2}>
      <Badge colorScheme="purple" fontSize="xs">
        {contactTypeLabels[contact.contactType] ?? contact.contactType}
      </Badge>
    </Td>
    <Td py={2} fontSize="xs" color="gray.500">
      {contact.durationMinutes != null ? `${contact.durationMinutes} min` : '—'}
    </Td>
    <Td py={2} fontSize="sm" color="gray.700" maxW="260px">
      <Text noOfLines={2}>{contact.clinicalSummary}</Text>
    </Td>
    <Td py={2} fontSize="xs" color="gray.600">
      {followUpLabels[contact.followUpAction] ?? contact.followUpAction}
    </Td>
  </Tr>
)

const ContactsForReviewPanel = () => {
  const navigate = useNavigate()
  const { dataArr: contacts, loading } = usePracticeContactsForReview()

  if (loading && !contacts) {
    return (
      <Center py={10}>
        <Spinner size="sm" color="green.400" />
      </Center>
    )
  }

  if (!contacts || contacts.length === 0) {
    return (
      <Center py={10}>
        <Text color="gray.500">No contacts flagged for billing review.</Text>
      </Center>
    )
  }

  return (
    <Box overflowX="auto" w="100%">
      <Box px={4} py={2} bg="purple.50" borderBottom="1px solid" borderColor="purple.100">
        <Text fontSize="xs" color="purple.700">
          These contacts were flagged by the provider as potentially billable telephone E&M. Review
          each to determine if a claim should be submitted.
        </Text>
      </Box>
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th py={2}>Date</Th>
            <Th py={2}>Patient</Th>
            <Th py={2}>Type</Th>
            <Th py={2}>Duration</Th>
            <Th py={2}>Clinical Summary</Th>
            <Th py={2}>Follow-Up</Th>
          </Tr>
        </Thead>
        <Tbody>
          {contacts.map(contact => (
            <ContactRow key={contact.id} contact={contact} navigate={navigate} />
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

// ─── §6.1/6.2/6.3/6.4.1: Combined encounter claims hook ─────────────────────
// One Firestore pass for all facility claims. Derived slices cover:
//   - pending attestation (Gap 1)
//   - submitted claims archive (Gap 2)
//   - per-payer timely filing days map (Gap 3)
//   - denial pattern data (§6.3)

const CLAIM_BATCH_SIZE = 30

const usePracticeEncounterClaims = (encounterIds: string[]) => {
  const [allClaims, setAllClaims] = useState<WithId<FacilityClaim>[] | null>(null)
  const [timelyFilingMap, setTimelyFilingMap] = useState<Map<string, number>>(new Map())
  const [loading, setLoading] = useState(false)
  const prevKeyRef = useRef<string>('')

  useEffect(() => {
    const key = [...encounterIds].sort().join(',')
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key

    if (encounterIds.length === 0) {
      setAllClaims([])
      setTimelyFilingMap(new Map())
      return
    }

    setLoading(true)
    const claimsRef = collection(db, CLAIMS) as CollectionReference<FacilityClaim>
    const chunks: string[][] = []
    for (let i = 0; i < encounterIds.length; i += CLAIM_BATCH_SIZE) {
      chunks.push(encounterIds.slice(i, i + CLAIM_BATCH_SIZE))
    }

    Promise.all(chunks.map(chunk => getDocs(query(claimsRef, where('encounterId', 'in', chunk)))))
      .then(async snapshots => {
        const claims: WithId<FacilityClaim>[] = []
        for (const snap of snapshots) {
          snap.docs.forEach(d => claims.push({ ...d.data(), id: d.id }))
        }
        setAllClaims(claims)

        // Fetch unique insurers to resolve per-payer timely filing days.
        const uniqueInsurerIds = [
          ...new Set(claims.map(c => c.insurerId).filter((id): id is string => !!id)),
        ]
        const insurerSnaps = await Promise.all(
          uniqueInsurerIds.map(id => getDoc(doc(db, INSURANCE_PROVIDERS, id))),
        )
        const insurerDaysById = new Map<string, number>()
        insurerSnaps.forEach((snap, i) => {
          if (snap.exists()) {
            const data = snap.data() as InsuranceProvider
            insurerDaysById.set(uniqueInsurerIds[i], data.timelyFilingDays ?? 90)
          }
        })
        // Map each encounter to its payer's timely filing days (first claim wins).
        const tfMap = new Map<string, number>()
        for (const claim of claims) {
          if (!tfMap.has(claim.encounterId) && claim.insurerId) {
            tfMap.set(claim.encounterId, insurerDaysById.get(claim.insurerId) ?? 90)
          }
        }
        setTimelyFilingMap(tfMap)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [encounterIds])

  const unattestedClaimEncounterIds = useMemo<Set<string>>(() => {
    if (!allClaims) return new Set()
    const s = new Set<string>()
    for (const c of allClaims) {
      if (!c.billingAttestation) s.add(c.encounterId)
    }
    return s
  }, [allClaims])

  const submittedClaims = useMemo(
    () => allClaims?.filter(c => !!c.billingAttestation) ?? [],
    [allClaims],
  )

  const claimsWithDenials = useMemo(
    () => allClaims?.filter(c => (c.denials?.length ?? 0) > 0) ?? [],
    [allClaims],
  )

  return {
    allClaims,
    unattestedClaimEncounterIds,
    submittedClaims,
    claimsWithDenials,
    timelyFilingMap,
    loading,
  }
}

interface DenialPattern {
  insurerId: string | null
  payerName: string
  claimCount: number
  totalDenials: number
  reasonCodeCounts: Record<string, number>
  lastDenialDate: number
}

const aggregateDenialPatterns = (claims: WithId<FacilityClaim>[]): DenialPattern[] => {
  const byPayer: Record<string, DenialPattern> = {}
  for (const claim of claims) {
    const key = claim.insurerId ?? '__unknown__'
    if (!byPayer[key]) {
      byPayer[key] = {
        insurerId: claim.insurerId,
        payerName: claim.insuranceCoverage || claim.insurerId || 'Unknown Payer',
        claimCount: 0,
        totalDenials: 0,
        reasonCodeCounts: {},
        lastDenialDate: 0,
      }
    }
    const p = byPayer[key]
    p.claimCount++
    for (const d of claim.denials ?? []) {
      p.totalDenials++
      p.reasonCodeCounts[d.reasonCode] = (p.reasonCodeCounts[d.reasonCode] ?? 0) + 1
      if (d.on > p.lastDenialDate) p.lastDenialDate = d.on
    }
  }
  return Object.values(byPayer).sort((a, b) => b.totalDenials - a.totalDenials)
}

const DenialPatternRow = ({ pattern }: { pattern: DenialPattern }) => {
  const [expanded, setExpanded] = useState(false)
  const sortedCodes = useMemo(
    () => Object.entries(pattern.reasonCodeCounts).sort(([, a], [, b]) => b - a),
    [pattern.reasonCodeCounts],
  )
  const topCode = sortedCodes[0]

  return (
    <>
      <Tr _hover={{ bg: 'gray.50', cursor: 'pointer' }} onClick={() => setExpanded(v => !v)}>
        <Td py={2} fontSize="sm" fontWeight={500} color="gray.800">
          {pattern.payerName}
        </Td>
        <Td py={2}>
          <Badge colorScheme="red" fontSize="xs">
            {pattern.claimCount}
          </Badge>
        </Td>
        <Td py={2}>
          <Badge colorScheme="orange" fontSize="xs">
            {pattern.totalDenials}
          </Badge>
        </Td>
        <Td py={2}>
          {topCode ? (
            <HStack spacing={1}>
              <Badge colorScheme="gray" fontSize="xs">
                {topCode[0]}
              </Badge>
              <Text fontSize="xs" color="gray.500">
                ×{topCode[1]}
              </Text>
            </HStack>
          ) : (
            <Text fontSize="xs" color="gray.400">
              —
            </Text>
          )}
        </Td>
        <Td py={2} fontSize="xs" color="gray.500">
          {pattern.lastDenialDate ? new Date(pattern.lastDenialDate).toLocaleDateString() : '—'}
        </Td>
        <Td py={2}>
          <Text fontSize="xs" color="blue.500">
            {expanded ? '▲ Hide' : '▼ Show all'}
          </Text>
        </Td>
      </Tr>
      {expanded && (
        <Tr>
          <Td colSpan={6} py={0} px={4} bg="gray.50">
            <Collapse in={expanded}>
              <Box py={2}>
                <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1}>
                  All denial reason codes — {pattern.payerName}
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {sortedCodes.map(([code, count]) => (
                    <HStack key={code} spacing={1}>
                      <Badge colorScheme="red" fontSize="xs">
                        {code}
                      </Badge>
                      <Text fontSize="xs" color="gray.600">
                        ×{count}
                      </Text>
                    </HStack>
                  ))}
                </HStack>
              </Box>
            </Collapse>
          </Td>
        </Tr>
      )}
    </>
  )
}

const DenialPatternsPanel = ({ claims }: { claims: WithId<FacilityClaim>[] }) => {
  const patterns = useMemo(() => aggregateDenialPatterns(claims), [claims])

  if (patterns.length === 0) {
    return (
      <Center py={10}>
        <Text color="gray.500">No denial records found for this practice's claims.</Text>
      </Center>
    )
  }

  return (
    <Box overflowX="auto" w="100%">
      <Box px={4} py={2} bg="red.50" borderBottom="1px solid" borderColor="red.100">
        <Text fontSize="xs" color="red.700">
          Aggregate denial data from all attested claims. Click a row to expand reason code
          breakdown. Use this to identify payer-specific denial trends over time.
        </Text>
      </Box>
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th py={2}>Payer</Th>
            <Th py={2}>Claims Denied</Th>
            <Th py={2}>Total Denials</Th>
            <Th py={2}>Top Reason Code</Th>
            <Th py={2}>Last Denial</Th>
            <Th py={2} />
          </Tr>
        </Thead>
        <Tbody>
          {patterns.map(p => (
            <DenialPatternRow key={p.insurerId ?? '__unknown__'} pattern={p} />
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

// ─── §6.1: Pending attestation panel ─────────────────────────────────────────

const PendingAttestationPanel = ({
  encounters,
  timelyFilingDaysMap,
}: {
  encounters: WithId<Encounter>[]
  timelyFilingDaysMap: Map<string, number>
}) => (
  <Box w="100%">
    <Box px={4} py={2} bg="orange.50" borderBottom="1px solid" borderColor="orange.100">
      <Text fontSize="xs" color="orange.700">
        Encounters with one or more claims that have not yet been attested by the billing team.
        Click a row to open claim review.
      </Text>
    </Box>
    <BillingEncounterTable
      encounters={encounters}
      timelyFilingDaysMap={timelyFilingDaysMap}
      emptyMessage="No claims pending attestation."
    />
  </Box>
)

// ─── §6.2: Submitted claims archive ──────────────────────────────────────────

const SubmittedClaimRow = ({
  claim,
  encounter,
  navigate,
}: {
  claim: WithId<FacilityClaim>
  encounter: WithId<Encounter> | undefined
  navigate: (path: string) => void
}) => (
  <Tr
    _hover={{ bg: 'blue.50', cursor: 'pointer' }}
    onClick={() => navigate(`/billing/${claim.encounterId}`)}>
    <Td py={2}>
      <PatientNameCell patientId={claim.patientId} />
    </Td>
    <Td py={2} fontSize="sm" color="gray.700">
      {encounter?.date ?? '—'}
    </Td>
    <Td py={2} fontSize="sm" color="gray.600">
      {claim.insuranceCoverage || '—'}
    </Td>
    <Td py={2} fontSize="sm" color="gray.600">
      {encounter ? (encounterTypeName[encounter.type] ?? encounter.type) : '—'}
    </Td>
    <Td py={2} fontSize="sm" color="gray.600">
      {claim.billingAttestation?.on
        ? new Date(claim.billingAttestation.on).toLocaleDateString()
        : '—'}
    </Td>
    <Td py={2}>
      <Badge colorScheme="green" fontSize="xs">
        ATTESTED
      </Badge>
    </Td>
  </Tr>
)

const SubmittedClaimsPanel = ({
  submittedClaims,
  encounterMap,
}: {
  submittedClaims: WithId<FacilityClaim>[]
  encounterMap: Map<string, WithId<Encounter>>
}) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return submittedClaims
    return submittedClaims.filter(c => {
      const payer = (c.insuranceCoverage ?? '').toLowerCase()
      const date = encounterMap.get(c.encounterId)?.date ?? ''
      return payer.includes(q) || date.includes(q)
    })
  }, [submittedClaims, search, encounterMap])

  if (submittedClaims.length === 0) {
    return (
      <Center py={10}>
        <Text color="gray.500">No submitted claims found.</Text>
      </Center>
    )
  }

  return (
    <Box w="100%">
      <Box px={4} py={2} bg="green.50" borderBottom="1px solid" borderColor="green.100">
        <HStack justify="space-between">
          <Text fontSize="xs" color="green.700">
            Attested claims archive — search by payer name or date of service.
          </Text>
          <Input
            size="xs"
            placeholder="Search by payer or DOS..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            bg="white"
            w="220px"
          />
        </HStack>
      </Box>
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th py={2}>Patient</Th>
              <Th py={2}>DOS</Th>
              <Th py={2}>Payer</Th>
              <Th py={2}>Visit Type</Th>
              <Th py={2}>Attested On</Th>
              <Th py={2}>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map(claim => (
              <SubmittedClaimRow
                key={claim.id}
                claim={claim}
                encounter={encounterMap.get(claim.encounterId)}
                navigate={navigate}
              />
            ))}
          </Tbody>
        </Table>
        {filtered.length === 0 && (
          <Center py={6}>
            <Text color="gray.400" fontSize="sm">
              No claims match &quot;{search}&quot;.
            </Text>
          </Center>
        )}
      </Box>
    </Box>
  )
}

// ─── Provider Hold ────────────────────────────────────────────────────────────

const TIMELY_FILING_ALERT_DAYS = 25

// §6.1 Provider Hold queue row for a held encounter. Display only — billing team gets
// no release control anywhere (only the provider can release, §1.6.2). Provider name
// comes from the authentication record (holds exist only on authenticated notes).
const ProviderHoldEncounterRow = ({ encounter }: { encounter: WithId<Encounter> }) => {
  const holdEntry = encounter.currentBillingHold
  const daysSinceDos = getDaysSinceDOS(encounter.date)
  const daysOnHold = holdEntry ? Math.floor((Date.now() - holdEntry.on) / (1000 * 60 * 60 * 24)) : 0
  const providerName = encounter.authentication
    ? `${encounter.authentication.providerFirst} ${encounter.authentication.providerLast}`
    : (encounter.renderingProviderInitials ?? encounter.renderingProvider ?? '—')
  // §1.6.2: the hold does not pause timely filing tracking — same alert as held visits.
  const isApproachingDeadline = daysSinceDos !== null && daysSinceDos >= TIMELY_FILING_ALERT_DAYS

  return (
    <Flex p={3} borderBottom="1px solid" borderColor="gray.100" flexFlow="column" gap={1}>
      <Flex align="center" gap={3} flexWrap="wrap">
        <Box flex={1} minW="160px">
          <PatientNameCell patientId={encounter.patientId} />
        </Box>
        <Text fontSize="sm" color="gray.600" minW="140px">
          {encounter.date ?? '—'}
        </Text>
        <Text fontSize="sm" color="gray.600" minW="120px">
          {providerName}
        </Text>
        <Badge colorScheme="orange" fontSize="xs">
          PROVIDER HOLD
        </Badge>
        <Badge colorScheme="orange" fontSize="xs" variant="outline">
          {daysOnHold}d on hold
        </Badge>
        {daysSinceDos !== null ? (
          <Badge
            colorScheme={daysSinceDos > 50 ? 'red' : daysSinceDos > 30 ? 'orange' : 'green'}
            fontSize="xs">
            {daysSinceDos}d since DOS
          </Badge>
        ) : null}
      </Flex>
      {holdEntry ? (
        <Text fontSize="xs" color="gray.500" pl={1}>
          Hold reason: {holdEntry.reason}
        </Text>
      ) : null}
      {isApproachingDeadline ? (
        <Text fontSize="xs" color="orange.600" fontWeight={500} pl={1}>
          Provider-held note approaching timely filing deadline — contact provider.
        </Text>
      ) : null}
    </Flex>
  )
}

const ProviderHoldRow = ({ visit }: { visit: WithId<PracticeVisit> }) => {
  const holdEntry = visit.currentBillingHold
  const daysSinceDos = useMemo(() => {
    const nowMs = Date.now()
    return Math.floor((nowMs - visit.startTime) / (1000 * 60 * 60 * 24))
  }, [visit.startTime])
  const isApproachingDeadline = daysSinceDos >= TIMELY_FILING_ALERT_DAYS

  return (
    <Flex p={3} borderBottom="1px solid" borderColor="gray.100" flexFlow="column" gap={1}>
      <Flex align="center" gap={3} flexWrap="wrap">
        <Text flex={1} minW="160px" fontWeight={500} fontSize="sm">
          {visit.patientLast}, {visit.patientFirst}
        </Text>
        <Text fontSize="sm" color="gray.600" minW="140px">
          {getDateString(visit.startTime)}
        </Text>
        <Badge colorScheme="orange" fontSize="xs">
          PROVIDER HOLD
        </Badge>
        <Badge
          colorScheme={daysSinceDos > 50 ? 'red' : daysSinceDos > 30 ? 'orange' : 'green'}
          fontSize="xs">
          {daysSinceDos}d since DOS
        </Badge>
      </Flex>
      {holdEntry ? (
        <Text fontSize="xs" color="gray.500" pl={1}>
          Hold reason: {holdEntry.reason}
        </Text>
      ) : null}
      {isApproachingDeadline ? (
        <Text fontSize="xs" color="orange.600" fontWeight={500} pl={1}>
          Provider-held note approaching timely filing deadline — contact provider.
        </Text>
      ) : null}
    </Flex>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export const BillingDashboard = () => {
  const { selectedPracticeId } = usePracticeAccess()

  const encountersQuery = useMemo(
    () => (selectedPracticeId ? getPracticeEncountersQuery(db, selectedPracticeId) : null),
    [selectedPracticeId],
  )
  const { dataArr: allEncounters, loading, error } = useQuery<Encounter>(encountersQuery)

  const visitsQuery = useMemo(() => {
    if (!selectedPracticeId) return null
    const visitsRef = collection(db, VISITS) as CollectionReference<PracticeVisit>
    return query(visitsRef, where('practiceId', '==', selectedPracticeId))
  }, [selectedPracticeId])
  const { dataArr: allVisits } = useQuery<PracticeVisit>(visitsQuery)

  const providerHoldVisits = useMemo(
    () => (allVisits ?? []).filter(v => !!v.currentBillingHold && !!v.authentication),
    [allVisits],
  )

  // §6.1: held encounters join the Provider Hold queue (holds exist only on
  // authenticated notes; the status filter is belt-and-braces).
  const providerHoldEncounters = useMemo(
    () =>
      (allEncounters ?? []).filter(
        enc => !!enc.currentBillingHold && enc.status === EncounterStatus.AUTHENTICATED,
      ),
    [allEncounters],
  )

  // §6.1: one queue for held visits + held encounters, sorted by hold date, oldest first.
  const providerHoldItems = useMemo(() => {
    const items: Array<
      | { kind: 'visit'; holdOn: number; visit: WithId<PracticeVisit> }
      | { kind: 'encounter'; holdOn: number; encounter: WithId<Encounter> }
    > = [
      ...providerHoldVisits.map(v => ({
        kind: 'visit' as const,
        holdOn: v.currentBillingHold?.on ?? 0,
        visit: v,
      })),
      ...providerHoldEncounters.map(enc => ({
        kind: 'encounter' as const,
        holdOn: enc.currentBillingHold?.on ?? 0,
        encounter: enc,
      })),
    ]
    return items.sort((a, b) => a.holdOn - b.holdOn)
  }, [providerHoldVisits, providerHoldEncounters])

  // Fetch practice data to get admin-configurable notification thresholds.
  const { data: practice } = useDocument<Practice>(MIDWIVES, selectedPracticeId ?? null)
  const thresholds = useMemo(
    () => ({
      firstNotice: practice?.notificationThresholds?.firstNotice ?? 45,
      criticalNotice: practice?.notificationThresholds?.criticalNotice ?? 52,
      billingAlert: practice?.notificationThresholds?.billingAlert ?? 59,
    }),
    [practice],
  )

  // §6.1/6.2/6.3: Batch-fetch all facility claims for the practice once.
  // Derives: pending attestation set, submitted claims, per-payer timely filing map.
  const allEncounterIds = useMemo(() => (allEncounters ?? []).map(e => e.id), [allEncounters])
  const { unattestedClaimEncounterIds, submittedClaims, claimsWithDenials, timelyFilingMap } =
    usePracticeEncounterClaims(allEncounterIds)

  // §6.2: Build encounter lookup for submitted claims archive cross-reference.
  const encounterMap = useMemo(() => {
    const m = new Map<string, WithId<Encounter>>()
    for (const enc of allEncounters ?? []) m.set(enc.id, enc)
    return m
  }, [allEncounters])

  // §1.6.2/§6.1: an active provider hold moves a note OUT of the standard review queues
  // (pending review + the derived pending-attestation slice) into the Provider Hold queue.
  const pendingReviewEncounters = useMemo(
    () =>
      (allEncounters ?? []).filter(
        enc =>
          (enc.status === EncounterStatus.COMPLETED ||
            enc.status === EncounterStatus.AUTHENTICATED) &&
          !enc.data?.doNotBill &&
          !enc.currentBillingHold,
      ),
    [allEncounters],
  )

  // §6.1: Encounters where at least one claim exists but is not yet attested.
  const pendingAttestationEncounters = useMemo(
    () => pendingReviewEncounters.filter(enc => unattestedClaimEncounterIds.has(enc.id)),
    [pendingReviewEncounters, unattestedClaimEncounterIds],
  )

  const authMonitoringEncounters = useMemo(
    () => (allEncounters ?? []).filter(enc => enc.status === EncounterStatus.COMPLETED),
    [allEncounters],
  )

  const amendmentAlertCount = useMemo(
    () =>
      pendingReviewEncounters.filter(
        enc =>
          (enc.amendments?.length ?? 0) > 0 &&
          enc.billingReviewedOn !== undefined &&
          (enc.amendments?.some(a => a.on > (enc.billingReviewedOn ?? 0)) ?? false),
      ).length,
    [pendingReviewEncounters],
  )

  const notificationCount = useMemo(
    () =>
      (allEncounters ?? []).filter(enc => {
        if (enc.status === EncounterStatus.AUTHENTICATED) return false
        const days = getDaysSinceDOS(enc.date)
        return days !== null && days >= thresholds.firstNotice
      }).length,
    [allEncounters, thresholds],
  )

  // §1.6.2: held notes must not be recommended for claim submission batches.
  const batchSourceEncounters = useMemo(
    () => (allEncounters ?? []).filter(enc => !enc.currentBillingHold),
    [allEncounters],
  )

  const batchCount = useMemo(
    () => computeBatchRecommendations(batchSourceEncounters).length,
    [batchSourceEncounters],
  )

  const { dataArr: contactsForReview } = usePracticeContactsForReview()
  const contactsForReviewCount = contactsForReview?.length ?? 0

  if (!selectedPracticeId) {
    return (
      <Center w="100%" py={20}>
        <Text color="gray.500">No practice selected. Go to Settings to select a practice.</Text>
      </Center>
    )
  }

  return (
    <VStack w="100%" h="100%" align="flex-start" spacing={0} bg="gray.50">
      <HStack
        w="100%"
        px={4}
        py={3}
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        justify="space-between">
        <Text fontWeight={700} fontSize="lg" color="gray.700">
          Billing Dashboard
        </Text>
        {amendmentAlertCount > 0 ? (
          <Badge colorScheme="red" fontSize="sm" px={2} py={0.5}>
            {amendmentAlertCount} amendment alert{amendmentAlertCount !== 1 ? 's' : ''}
          </Badge>
        ) : null}
      </HStack>

      {loading && !allEncounters ? (
        <Center w="100%" py={20}>
          <Spinner size="lg" color="green.400" />
        </Center>
      ) : error ? (
        <Center w="100%" py={20}>
          <Text color="red.500">{error}</Text>
        </Center>
      ) : (
        <Box w="100%" flex={1} overflow="auto" p={4}>
          <Tabs variant="enclosed" colorScheme="green" isLazy>
            <TabList bg="white" borderTopRadius="md" borderBottom="none">
              <Tab fontSize="sm" fontWeight={500}>
                Pending Review
                {pendingReviewEncounters.length > 0 ? (
                  <Badge ml={2} colorScheme="green" fontSize="xs">
                    {pendingReviewEncounters.length}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Pending Attestation
                {pendingAttestationEncounters.length > 0 ? (
                  <Badge ml={2} colorScheme="orange" fontSize="xs">
                    {pendingAttestationEncounters.length}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Submitted Claims
                {submittedClaims.length > 0 ? (
                  <Badge ml={2} colorScheme="green" fontSize="xs">
                    {submittedClaims.length}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Auth Monitoring
                {authMonitoringEncounters.length > 0 ? (
                  <Badge ml={2} colorScheme="yellow" fontSize="xs">
                    {authMonitoringEncounters.length}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Notifications
                {notificationCount > 0 ? (
                  <Badge ml={2} colorScheme="red" fontSize="xs">
                    {notificationCount}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Batch Recommendations
                {batchCount > 0 ? (
                  <Badge ml={2} colorScheme="blue" fontSize="xs">
                    {batchCount}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Contacts for Review
                {contactsForReviewCount > 0 ? (
                  <Badge ml={2} colorScheme="purple" fontSize="xs">
                    {contactsForReviewCount}
                  </Badge>
                ) : null}
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Denial Patterns
              </Tab>
              <Tab fontSize="sm" fontWeight={500}>
                Provider Hold
                {providerHoldItems.length > 0 ? (
                  <Badge ml={2} colorScheme="orange" fontSize="xs">
                    {providerHoldItems.length}
                  </Badge>
                ) : null}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <BillingEncounterTable
                    encounters={pendingReviewEncounters}
                    timelyFilingDaysMap={timelyFilingMap}
                    emptyMessage="No encounters pending review"
                  />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <PendingAttestationPanel
                    encounters={pendingAttestationEncounters}
                    timelyFilingDaysMap={timelyFilingMap}
                  />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <SubmittedClaimsPanel
                    submittedClaims={submittedClaims}
                    encounterMap={encounterMap}
                  />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <BillingEncounterTable
                    encounters={authMonitoringEncounters}
                    timelyFilingDaysMap={timelyFilingMap}
                    emptyMessage="No encounters awaiting authentication"
                  />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <NotificationLogPanel encounters={allEncounters ?? []} thresholds={thresholds} />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <BatchRecommendationsPanel encounters={batchSourceEncounters} />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <ContactsForReviewPanel />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  <DenialPatternsPanel claims={claimsWithDenials} />
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                <Box bg="white" borderBottomRadius="md" boxShadow="sm" overflow="hidden">
                  {providerHoldItems.length === 0 ? (
                    <Center py={10}>
                      <Text color="gray.500">No notes on provider hold.</Text>
                    </Center>
                  ) : (
                    providerHoldItems.map(item =>
                      item.kind === 'visit' ? (
                        <ProviderHoldRow key={`visit-${item.visit.id}`} visit={item.visit} />
                      ) : (
                        <ProviderHoldEncounterRow
                          key={`encounter-${item.encounter.id}`}
                          encounter={item.encounter}
                        />
                      ),
                    )
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}
    </VStack>
  )
}
